import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MOBILE_QUERY } from "../hooks/useIsMobile";
import { KINDS, buildPrompt, screenshotNames } from "./prompt.mjs";
import { installTrail, recordRoute, snapshotTrail } from "./trail";
import "./feedback.css";

// The floating feedback icon, bottom-right on every signed-in screen. A click
// snaps the screen, notes where Gabriel is and what he did just before, and
// gives him a box to say what's wrong. "Copy for Claude Code" puts the whole
// report on the clipboard as one prompt and saves the screenshot to Downloads,
// where the prompt tells Claude to look for it.
//
// Nothing is sent anywhere: the clipboard and the Downloads folder are the
// whole transport.

const MAX_SHOTS = 3;

// The Shell (and this button with it) remounts on every route change, so an
// unfinished report lives here rather than in component state: navigating to
// show Claude a second page mustn't throw away what was already written.
const draft = { kind: "bug", message: "", shots: [], where: null };

const textOf = (sel) => {
  const el = document.querySelector(sel);
  return el ? el.textContent.replace(/\s+/g, " ").trim().slice(0, 120) : null;
};

// What's on screen, read off the DOM at the moment the icon is clicked.
function whereNow() {
  const build = [...document.scripts].map((s) => s.src).find((s) => /\/static\/js\/main\.[^/]+\.js$/.test(s));
  const dialogs = [...document.querySelectorAll('[role="dialog"]')]
    .filter((d) => !d.closest(".fb-root"))
    .map((d) => d.getAttribute("aria-label") || (d.querySelector("h1,h2,h3") || {}).textContent)
    .filter(Boolean)
    .map((s) => s.trim().slice(0, 80));
  const selection = String(window.getSelection ? window.getSelection() : "").replace(/\s+/g, " ").trim();
  return {
    path: window.location.pathname,
    search: window.location.search,
    heading: textOf(".crm-title"),
    record: textOf(".rec-name"),
    dialogs,
    selection: selection ? selection.slice(0, 300) : null,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    mobile: window.matchMedia(MOBILE_QUERY).matches,
    build: build ? build.split("/").pop() : null,
    when: new Date().toLocaleString("en-GB", {
      weekday: "short", day: "numeric", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZoneName: "short",
    }),
  };
}

// What's on screen as a PNG, minus this widget. The page scrolls at the window,
// so the full-body render is cropped to the part that was in view. Lists that
// scroll inside their own box render from their top: html-to-image clones the
// DOM and a clone has no scroll position. The paste route covers that case.
// Fonts are skipped: embedding Poppins from Google Fonts took 30s+, so text
// renders in a fallback face and wraps a little differently than on screen.
async function captureScreen() {
  const { toCanvas } = await import("html-to-image");
  const full = await toCanvas(document.body, {
    pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
    skipFonts: true,
    imagePlaceholder: "data:image/gif;base64,R0lGODlhAQABAAAAACw=",
    // The widget itself, and the <noscript> line a clone would otherwise print.
    filter: (node) => node.nodeName !== "NOSCRIPT" && !(node.classList && node.classList.contains("fb-root")),
    backgroundColor: getComputedStyle(document.body).backgroundColor || "#FAFAF7",
  });
  const ratio = full.width / document.body.scrollWidth || 1;
  const top = Math.round(window.scrollY * ratio);
  const w = Math.min(full.width, Math.round(window.innerWidth * ratio));
  const h = Math.min(full.height - top, Math.round(window.innerHeight * ratio));
  const c = document.createElement("canvas");
  c.width = w;
  c.height = h;
  c.getContext("2d").drawImage(full, 0, top, w, h, 0, 0, w, h);
  return c.toDataURL("image/png");
}

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });

function download(dataUrl, name) {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// The async clipboard API is refused in some contexts (an embedded pane, a
// lost focus); the old execCommand route still works in most of them.
async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}

async function copyImage(dataUrl) {
  const blob = await (await fetch(dataUrl)).blob();
  await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
}

export default function FeedbackButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(draft.kind);
  const [message, setMessage] = useState(draft.message);
  const [shots, setShots] = useState(draft.shots);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null); // { names } after a copy
  const textRef = useRef(null);

  useEffect(() => { installTrail(); }, []);
  useEffect(() => { recordRoute(location.pathname + location.search); }, [location.pathname, location.search]);

  // Keep the module draft in step so a remount picks up where this left off.
  useEffect(() => { Object.assign(draft, { kind, message, shots }); }, [kind, message, shots]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => textRef.current && textRef.current.focus(), 30);
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { clearTimeout(t); window.removeEventListener("keydown", onKey); };
  }, [open]);

  const snap = async () => {
    setCapturing(true);
    setError(null);
    try {
      const img = await Promise.race([
        captureScreen(),
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000)),
      ]);
      setShots((s) => (s.length >= MAX_SHOTS ? s : [...s, img]));
    } catch {
      setError("Couldn't capture the screen. Paste one instead: Win+Shift+S, then Ctrl+V in the box.");
    } finally {
      setCapturing(false);
    }
  };

  const openPanel = () => {
    // Where and when are fixed at the first open of a report, so a second page
    // visited to show more doesn't overwrite where the problem actually was.
    if (!draft.where) draft.where = whereNow();
    setCopied(null);
    setError(null);
    setOpen(true);
    if (!draft.shots.length) snap();
  };

  const reset = () => {
    Object.assign(draft, { kind: "bug", message: "", shots: [], where: null });
    setKind("bug");
    setMessage("");
    setShots([]);
    setCopied(null);
    setOpen(false);
  };

  const onPaste = async (e) => {
    const items = [...((e.clipboardData && e.clipboardData.items) || [])].filter((i) => i.type.startsWith("image/"));
    if (!items.length) return;
    e.preventDefault();
    for (const it of items) {
      const f = it.getAsFile();
      if (f) {
        const url = await fileToDataUrl(f);
        setShots((s) => (s.length >= MAX_SHOTS ? s : [...s, url]));
      }
    }
  };

  const copyForClaude = async () => {
    setError(null);
    const at = Date.now();
    const names = screenshotNames(shots.length, at);
    const trail = snapshotTrail();
    const text = buildPrompt({
      kind,
      message,
      where: draft.where || whereNow(),
      actions: trail.actions.slice(-25),
      requests: trail.requests,
      errors: trail.errors,
      screenshots: names,
      at,
    });
    if (!(await copyText(text))) {
      setError("The browser blocked the clipboard. Click the page once and try again.");
      return;
    }
    shots.forEach((s, i) => download(s, names[i]));
    setCopied({ names });
  };

  const copyShot = async (i) => {
    try {
      await copyImage(shots[i]);
      setCopied((c) => ({ ...(c || { names: [] }), image: i }));
    } catch {
      setError("The browser wouldn't copy the image. It's still saved to Downloads.");
    }
  };

  const hint = (KINDS.find((k) => k.id === kind) || KINDS[0]).hint;
  const where = draft.where;
  const n = snapshotTrail();
  const counts = [
    `${n.actions.length} step${n.actions.length === 1 ? "" : "s"}`,
    `${n.requests.length} API call${n.requests.length === 1 ? "" : "s"}`,
    n.errors.length ? `${n.errors.length} error${n.errors.length === 1 ? "" : "s"}` : null,
  ].filter(Boolean);

  return (
    <div className="fb-root">
      {!open && (
        <button
          className={`fb-trigger${message || shots.length ? " fb-trigger--draft" : ""}`}
          onClick={openPanel}
          title="Report a bug, something missing, or an idea"
          aria-label="Feedback"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {open && (
        <div className="fb-panel" role="dialog" aria-label="Feedback for Claude Code">
          <div className="fb-head">
            <span className="fb-title">Feedback</span>
            <button className="fb-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
          </div>

          <div className="fb-body">
            <div className="fb-kinds" role="radiogroup" aria-label="Kind">
              {KINDS.map((k) => (
                <button key={k.id} role="radio" aria-checked={kind === k.id} className={kind === k.id ? "on" : ""} onClick={() => setKind(k.id)}>
                  {k.label}
                </button>
              ))}
            </div>

            <textarea
              ref={textRef}
              className="fb-text"
              value={message}
              onChange={(e) => { setMessage(e.target.value); setCopied(null); }}
              onPaste={onPaste}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) copyForClaude(); }}
              placeholder={hint}
              rows={5}
            />

            <div className="fb-shots">
              {shots.map((s, i) => (
                <div key={i} className="fb-shot">
                  <img src={s} alt={`Screenshot ${i + 1}`} />
                  <button className="fb-shot__x" onClick={() => { setShots((x) => x.filter((_, j) => j !== i)); setCopied(null); }} aria-label="Remove screenshot">×</button>
                  <button className="fb-shot__copy" onClick={() => copyShot(i)} title="Copy this image to paste it straight into Claude Code">
                    {copied && copied.image === i ? "Copied" : "Copy"}
                  </button>
                </div>
              ))}
              {capturing && <div className="fb-shot fb-shot--busy">Capturing…</div>}
              {!capturing && shots.length < MAX_SHOTS && (
                <button className="fb-shot fb-shot--add" onClick={snap} title="Take another screenshot of the page">
                  + Snap
                </button>
              )}
            </div>

            <div className="fb-auto">
              <b>Attached:</b> {where ? (where.record ? `${where.heading || where.path} · ${where.record}` : where.heading || where.path) : "this page"}
              {" · "}{counts.join(" · ")}
              <span className="fb-auto__hint"> · paste a screenshot to add one</span>
            </div>

            {error && <div className="fb-error">{error}</div>}

            {copied && copied.names && (
              <div className="fb-done">
                Copied. Paste it into Claude Code with Ctrl+V.
                {copied.names.length > 0 && ` The screenshot${copied.names.length > 1 ? "s are" : " is"} in Downloads.`}
              </div>
            )}

            <div className="fb-foot">
              <button className="fb-btn-ghost" onClick={reset} title="Throw this report away and start fresh next time">
                {copied ? "Done" : "Discard"}
              </button>
              <button className="fb-btn" onClick={copyForClaude} disabled={!message.trim() || capturing}>
                Copy for Claude Code
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
